import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import {
  Trophy,
  Loader2,
  ArrowLeft,
  Upload,
  X,
} from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import {
  Button,
  Card,
  FormField,
  InfoBanner,
  StatusPill,
} from '../components/ui';
import {
  BALL_TYPE_OPTIONS,
  DAY_NAMES,
  emptyTournamentForm,
  formToApiBody,
  listMatchDayOccurrences,
  requiredAvailabilityCount,
  tournamentToForm,
} from '../features/tournament/tournamentFormUtils';
import { resolveUploadUrl } from '../utils/uploadUrl';

const API_BASE = API_BASE_URL;

const authHeaders = (json = true) => {
  const token = localStorage.getItem('auth_token');
  const fid = localStorage.getItem('active_facility_id');
  return {
    ...(json && { 'Content-Type': 'application/json' }),
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(fid && { 'X-Facility-Id': fid }),
  };
};

const inputCls = 'ga-input w-full';

function normalizeQrPath(path) {
  if (path == null || String(path).trim() === '') return null;
  return String(path).trim();
}

function applyTournamentToForm(tournament, setters) {
  const { setForm, setExistingQrUrl, setQrFile, setQrPreview, setQrImageVisible } = setters;
  setForm(tournamentToForm(tournament));
  setExistingQrUrl(normalizeQrPath(tournament.upi_qr_image_url));
  setQrFile(null);
  setQrPreview(null);
  setQrImageVisible(false);
}

const TournamentsDashboard = () => {
  const navigate = useNavigate();
  const { tournamentId } = useParams();
  const requestedTournamentId = tournamentId ? Number(tournamentId) : null;
  const [loading, setLoading] = useState(Boolean(requestedTournamentId));
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyTournamentForm);
  const [qrFile, setQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const [existingQrUrl, setExistingQrUrl] = useState(null);
  const [qrImageVisible, setQrImageVisible] = useState(false);
  const qrInputRef = useRef(null);
  const detailRequestIdRef = useRef(0);

  const weekPreview = useMemo(() => {
    const weeks = listMatchDayOccurrences(
      form.tournament_starts_at,
      form.tournament_ends_at,
      form.match_day_of_week
    );
    const required = requiredAvailabilityCount(form.matches_per_team);
    return { weeks, required, total: weeks.length };
  }, [
    form.tournament_starts_at,
    form.tournament_ends_at,
    form.match_day_of_week,
    form.matches_per_team,
  ]);

  useEffect(() => {
    return () => {
      if (qrPreview) URL.revokeObjectURL(qrPreview);
    };
  }, [qrPreview]);

  useEffect(() => {
    const requestId = ++detailRequestIdRef.current;
    if (!requestedTournamentId) {
      setLoading(false);
      setError('');
      setForm(emptyTournamentForm());
      setExistingQrUrl(null);
      return undefined;
    }

    setLoading(true);
    setError('');
    fetch(`${API_BASE}/tournaments/${requestedTournamentId}`, { headers: authHeaders() })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          throw new Error(data?.message || 'Failed to load tournament');
        }
        if (requestId !== detailRequestIdRef.current) return;
        applyTournamentToForm(data.data, {
          setForm,
          setExistingQrUrl,
          setQrFile,
          setQrPreview,
          setQrImageVisible,
        });
      })
      .catch((err) => {
        if (requestId === detailRequestIdRef.current) {
          setError(err.message || 'Failed to load tournament');
        }
      })
      .finally(() => {
        if (requestId === detailRequestIdRef.current) setLoading(false);
      });

    return () => {
      detailRequestIdRef.current += 1;
    };
  }, [requestedTournamentId]);

  const uploadQr = async (tournamentId) => {
    if (!qrFile) return;
    const fd = new FormData();
    fd.append('qr', qrFile);
    const res = await fetch(`${API_BASE}/tournaments/${tournamentId}/upi-qr`, {
      method: 'POST',
      headers: authHeaders(false),
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(data?.message || 'Failed to upload UPI QR');
    }
    setExistingQrUrl(normalizeQrPath(data.data?.upi_qr_image_url));
    setQrFile(null);
    setQrPreview(null);
    setQrImageVisible(false);
    if (qrInputRef.current) qrInputRef.current.value = '';
  };

  const saveTournament = async (publish) => {
    if (!form.name.trim()) {
      setFormError('Tournament name is required');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const body = formToApiBody(form, { publish });
      const isEdit = Boolean(requestedTournamentId);
      const url = isEdit
        ? `${API_BASE}/tournaments/${requestedTournamentId}`
        : `${API_BASE}/tournaments`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data?.message || 'Failed to save tournament');
      }
      const savedId = data.data.id;
      if (qrFile) await uploadQr(savedId);
      navigate(`/dashboard/tournaments/${savedId}`);
    } catch (err) {
      setFormError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const persistedQrUrl = normalizeQrPath(existingQrUrl);

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-5xl px-4 py-6">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <Link
              to={requestedTournamentId
                ? `/dashboard/tournaments/${requestedTournamentId}`
                : '/dashboard/tournaments'}
              className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-ga-text-muted hover:text-ga-text-primary"
            >
              <ArrowLeft size={16} /> Back to tournaments
            </Link>
            <h1 className="flex items-center gap-2 text-xl font-bold text-ga-text-primary">
              <Trophy size={26} className="text-ga-primary" />
              {requestedTournamentId ? 'Edit tournament' : 'Register Tournament'}
            </h1>
            <p className="mt-1 text-sm text-ga-text-muted">
              Configure the tournament schedule, fees, registration details, and public information.
            </p>
          </div>
        </div>

        {error && (
          <InfoBanner variant="danger" className="mb-4">
            {error}
          </InfoBanner>
        )}

        {requestedTournamentId && loading && (
          <div className="py-10 text-center text-sm text-ga-text-muted">
            <Loader2 size={28} className="mx-auto mb-2 block animate-spin" />
            Loading tournament…
          </div>
        )}

        {!loading && !error && (
          <div className="mb-6 rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface shadow-ga-soft">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ga-border-subtle px-4 py-3">
              <div className="flex items-center gap-2">
                {requestedTournamentId && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormError('');
                      navigate(`/dashboard/tournaments/${requestedTournamentId}`);
                    }}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-ga-text-muted hover:text-ga-text-primary"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                )}
                <h2 className="text-sm font-bold text-ga-text-primary">
                  {requestedTournamentId ? 'Edit tournament' : 'Create tournament'}
                </h2>
              </div>
              <StatusPill
                status={form.is_published ? 'confirmed' : 'pending'}
                label={form.is_published ? 'Published' : 'Draft'}
                size="sm"
              />
            </div>

            {formError && (
              <div className="px-4 pt-3">
                <InfoBanner variant="danger">{formError}</InfoBanner>
              </div>
            )}

            <div className="space-y-4 p-4">
              <Card className="border-ga-border-subtle bg-ga-bg-surface-subtle/40 p-4">
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-ga-text-muted">
                  Basic Info
                </h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Tournament name" required className="sm:col-span-2">
                    <input
                      className={inputCls}
                      placeholder="e.g. Summer Cup 2026"
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                    />
                  </FormField>
                  <FormField label="Registration opens">
                    <input
                      type="date"
                      className={inputCls}
                      value={form.registration_starts_at}
                      onChange={(e) => setField('registration_starts_at', e.target.value)}
                    />
                  </FormField>
                  <FormField label="Registration closes">
                    <input
                      type="date"
                      className={inputCls}
                      value={form.registration_ends_at}
                      onChange={(e) => setField('registration_ends_at', e.target.value)}
                    />
                  </FormField>
                  <FormField label="Tournament starts" required>
                    <input
                      type="date"
                      className={inputCls}
                      value={form.tournament_starts_at}
                      onChange={(e) => setField('tournament_starts_at', e.target.value)}
                    />
                  </FormField>
                  <FormField label="Tournament ends" required>
                    <input
                      type="date"
                      className={inputCls}
                      value={form.tournament_ends_at}
                      onChange={(e) => setField('tournament_ends_at', e.target.value)}
                    />
                  </FormField>
                  <FormField label="Max teams">
                    <input
                      className={inputCls}
                      placeholder="e.g. 16"
                      value={form.max_teams}
                      onChange={(e) =>
                        setField('max_teams', e.target.value.replace(/\D/g, ''))
                      }
                    />
                  </FormField>
                </div>
              </Card>

              <Card className="border-ga-border-subtle bg-ga-bg-surface-subtle/40 p-4">
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-ga-text-muted">
                  Format &amp; Schedule
                </h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <FormField label="Ball type" required>
                    <select
                      className={inputCls}
                      value={form.ball_type}
                      onChange={(e) => setField('ball_type', e.target.value)}
                    >
                      {BALL_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <FormField label="Overs per match" required>
                    <input
                      className={inputCls}
                      inputMode="numeric"
                      value={form.overs_per_match}
                      onChange={(e) =>
                        setField('overs_per_match', e.target.value.replace(/\D/g, ''))
                      }
                    />
                  </FormField>
                  <FormField label="League matches per team" required>
                    <input
                      className={inputCls}
                      inputMode="numeric"
                      value={form.matches_per_team}
                      onChange={(e) =>
                        setField('matches_per_team', e.target.value.replace(/\D/g, ''))
                      }
                    />
                  </FormField>
                  <FormField label="Match day of week" required>
                    <select
                      className={inputCls}
                      value={form.match_day_of_week}
                      onChange={(e) => setField('match_day_of_week', e.target.value)}
                    >
                      {DAY_NAMES.map((name, idx) => (
                        <option key={name} value={String(idx)}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <FormField label="Playoff qualifiers">
                    <input
                      className={inputCls}
                      inputMode="numeric"
                      placeholder="Optional"
                      value={form.playoff_qualifiers}
                      onChange={(e) =>
                        setField('playoff_qualifiers', e.target.value.replace(/\D/g, ''))
                      }
                    />
                  </FormField>
                </div>
                {weekPreview.total > 0 && weekPreview.required > 0 && (
                  <div className="mt-3 rounded-ga-sm border border-ga-primary/30 bg-ga-primary-soft px-3 py-2 text-sm text-ga-text-primary">
                    Teams will select{' '}
                    <strong>
                      {weekPreview.required} of {weekPreview.total}
                    </strong>{' '}
                    {DAY_NAMES[Number(form.match_day_of_week)] || 'match'} dates
                  </div>
                )}
              </Card>

              <Card className="border-ga-border-subtle bg-ga-bg-surface-subtle/40 p-4">
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-ga-text-muted">
                  Fees &amp; Payment
                </h3>

                <div className="mb-4 rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-3 py-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ga-text-muted">
                    Fee structure per match
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FormField label="Morning match (₹)">
                      <input
                        className={inputCls}
                        inputMode="numeric"
                        placeholder="e.g. 5000"
                        value={form.registration_fee}
                        onChange={(e) =>
                          setField('registration_fee', e.target.value.replace(/\D/g, ''))
                        }
                      />
                    </FormField>
                    <FormField label="Second slot (₹)">
                      <input
                        className={inputCls}
                        inputMode="numeric"
                        placeholder="e.g. 4000"
                        value={form.registration_fee_afternoon}
                        onChange={(e) =>
                          setField(
                            'registration_fee_afternoon',
                            e.target.value.replace(/\D/g, '')
                          )
                        }
                      />
                    </FormField>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Registration amount (₹)">
                    <input
                      className={inputCls}
                      inputMode="numeric"
                      placeholder="e.g. 1000"
                      value={form.registration_amount}
                      onChange={(e) =>
                        setField('registration_amount', e.target.value.replace(/\D/g, ''))
                      }
                    />
                  </FormField>
                  <FormField label="Security amount (₹)">
                    <input
                      className={inputCls}
                      inputMode="numeric"
                      placeholder="e.g. 5000"
                      value={form.security_amount}
                      onChange={(e) =>
                        setField('security_amount', e.target.value.replace(/\D/g, ''))
                      }
                    />
                  </FormField>
                  <FormField label="UPI ID" className="sm:col-span-2">
                    <input
                      className={inputCls}
                      placeholder="name@upi"
                      value={form.upi_id}
                      onChange={(e) => setField('upi_id', e.target.value)}
                    />
                  </FormField>
                </div>
                <FormField label="UPI QR code" className="mt-3">
                  <input
                    ref={qrInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setQrFile(file || null);
                      if (file) {
                        setQrPreview(URL.createObjectURL(file));
                        setQrImageVisible(true);
                      } else {
                        setQrPreview(null);
                      }
                    }}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => qrInputRef.current?.click()}
                      className="text-xs"
                    >
                      <Upload size={14} /> {qrFile ? 'Change QR' : 'Upload QR'}
                    </Button>
                    {qrFile && (
                      <button
                        type="button"
                        onClick={() => {
                          setQrFile(null);
                          setQrPreview(null);
                          if (qrInputRef.current) qrInputRef.current.value = '';
                        }}
                        className="rounded p-1 text-ga-text-muted hover:bg-ga-bg-surface-subtle"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  {qrPreview && (
                    <img
                      src={qrPreview}
                      alt="UPI QR preview"
                      className="mt-2 h-32 w-32 rounded-ga-sm border border-ga-border-subtle object-contain bg-white"
                    />
                  )}
                  {!qrPreview && persistedQrUrl && (
                    <img
                      src={resolveUploadUrl(persistedQrUrl)}
                      alt="UPI QR preview"
                      className={`mt-2 h-32 w-32 rounded-ga-sm border border-ga-border-subtle object-contain bg-white ${qrImageVisible ? '' : 'hidden'}`}
                      onLoad={() => setQrImageVisible(true)}
                      onError={() => setQrImageVisible(false)}
                    />
                  )}
                </FormField>
              </Card>

              <Card className="border-ga-border-subtle bg-ga-bg-surface-subtle/40 p-4">
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-ga-text-muted">
                  Format / Rules / Prizes
                </h3>
                <div className="grid gap-3 lg:grid-cols-3">
                  <FormField label="Format">
                    <textarea
                      className={`${inputCls} min-h-[120px] resize-y`}
                      placeholder="Format details shown on enrollment page"
                      value={form.format_details}
                      onChange={(e) => setField('format_details', e.target.value)}
                    />
                  </FormField>
                  <FormField label="Rules">
                    <textarea
                      className={`${inputCls} min-h-[120px] resize-y`}
                      placeholder="Rules shown on enrollment page"
                      value={form.rules_details}
                      onChange={(e) => setField('rules_details', e.target.value)}
                    />
                  </FormField>
                  <FormField label="Prizes">
                    <textarea
                      className={`${inputCls} min-h-[120px] resize-y`}
                      placeholder="Prizes shown on enrollment page"
                      value={form.prizes_details}
                      onChange={(e) => setField('prizes_details', e.target.value)}
                    />
                  </FormField>
                </div>
              </Card>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-ga-border-subtle px-4 py-3">
              <Button
                type="button"
                variant="ghost"
                disabled={saving}
                onClick={() => saveTournament(false)}
              >
                {saving ? 'Saving…' : 'Save draft'}
              </Button>
              <Button type="button" disabled={saving} onClick={() => saveTournament(true)}>
                {saving ? 'Publishing…' : 'Publish'}
              </Button>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

export default TournamentsDashboard;
